// Shared map of an overview: every selected corridor's centreline, the filtered
// deficiency sites and the multi-deficiency cluster rings. Same base maps, layer
// control, popups and focus protocol as the Safety Dashboard's map; no crash data.
// Receives ALREADY-FILTERED arrays. SVG renderer on purpose: Leaflet's canvas
// renderer in several panes lets only the top pane receive clicks.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import {
  MapContainer, TileLayer, LayersControl, LayerGroup, Marker, Pane, Polyline, CircleMarker, Popup, Rectangle, Tooltip, GeoJSON, useMap, useMapEvents,
} from 'react-leaflet';
import DemoBasemap from '../Safety/DemoBasemap';
import StationsLayer, { DETAIL_ANALYSES, DETAIL_COLORS, GLR_BANDS, PATH_COLOR, SHAPES } from './StationsLayer';
import 'leaflet/dist/leaflet.css';
import { ANALYSIS_COLORS, ANALYSIS_NAMES, SEVERITY_COLORS, segmentName } from './overviewModel';
import { ManeuverButtons, ManeuverRows } from '../Safety/ManeuverLinks';
import CrashPopupBody, { CRASH_COLORS, crashPathOptions, crashRadius } from '../Safety/CrashPopup';
import { GUARDRAIL_LABEL, NET_CONDITIONS, NET_SOURCES } from '../SafetyOverview/rules';
import TopNSelect from '../Safety/TopNSelect';
import { StatusChip } from '../SafetyOverview/SiteNoteDialog';
import { STATUS_COLOR, STATUS_LABEL } from '../SafetyOverview/siteNotes';

const fmtMp = (v) => (v == null ? '—' : Number(v).toFixed(2));
const esc = (t) => String(t == null ? '' : t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// Road-network features (Safety Overview Part B): a letter per SOP condition, ringed in the
// colour of the source it was taken from
export const NET_SOURCE_COLORS = { ours: '#0d9488', dot: '#2563eb', osm: '#65a30d' };
const NET_GLYPH = { signal: 'S', majorIntersection: '+', busStop: 'B', crosswalk: 'C', school: 'Sc', emergency: 'H',
  roundabout: '○', railCrossing: 'RR', median: 'M' };
const NET_LABEL = { ...Object.fromEntries(NET_CONDITIONS.map(([k, label]) => [k, label])), guardrail: GUARDRAIL_LABEL };
const GUARDRAIL_COLOR = '#1e3a8a';          // existing guardrail beams: dark blue, on a light casing
const NET_SOURCE_NAME = Object.fromEntries(NET_SOURCES.map(([k, label]) => [k, label]));
const netIcons = {};
const netIcon = (f) => {
  const k = `${f.type}|${f.src}`;
  if (!netIcons[k]) {
    netIcons[k] = L.divIcon({ className: 'so-net-icon', iconSize: [20, 20], iconAnchor: [10, 10],
      html: `<span style="border-color:${NET_SOURCE_COLORS[f.src] || '#64748b'}">${NET_GLYPH[f.type] || '?'}</span>` });
  }
  return netIcons[k];
};

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => { if (bounds) map.fitBounds(bounds, { padding: [30, 30] }); }, [map, bounds]);
  return null;
}

// Popups keep clear of what they describe and of the floating panels: a deficiency's popup opens above its
// whole extent (its bounding box), and the map pans so popups stay off the Top on map panel and the legend.
// wide enough for a label and its value on one line (the content is no longer held to 16rem)
const POPUP_OPTS = { autoPanPaddingTopLeft: [24, 150], autoPanPaddingBottomRight: [24, 24], offset: [0, -12], minWidth: 240, maxWidth: 460 };
const SoPopup = (props) => <Popup {...POPUP_OPTS} {...props} />;
/** [[south, west], [north, east]] of an item drawn as a line (a site, a hotspot), or null for a point. */
export function boundsOf(item) {
  const pts = (item && item.coords) || [];
  if (pts.length < 2) return null;
  const lats = pts.map((p) => p[0]);
  const lngs = pts.map((p) => p[1]);
  return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
}
/** A line's popup opens at the top middle of its extent, so it never covers the line. */
const popupAboveLine = { popupopen: (e) => {
  const b = e.target.getBounds ? e.target.getBounds() : null;
  if (b && b.isValid()) e.popup.setLatLng([b.getNorth(), b.getCenter().lng]);
} };
/** The selection box: the item's extent with a margin, at least ~40 m each way so a straight run still shows a box. */
function selectionBox(bounds) {
  const [[s, w], [n, e]] = bounds;
  const lat = (s + n) / 2;
  const minLat = 0.00036;
  const minLng = minLat / Math.max(0.2, Math.cos((lat * Math.PI) / 180));
  const padLat = Math.max((n - s) * 0.12, minLat);
  const padLng = Math.max((e - w) * 0.12, minLng);
  return [[s - padLat, w - padLng], [n + padLat, e + padLng]];
}

function FlyToFocus({ focus, refs }) {
  const map = useMap();
  useEffect(() => {
    if (!focus || focus.lat == null) return undefined;
    const target = focus.id ? refs.current[focus.id] : null;
    const size = map.getSize();
    const box = focus.bounds ? selectionBox(focus.bounds) : null;
    // The deficiency comes first: it lands in the lower part of the map and stays there. Its popup opens above it
    // WITHOUT panning the map (a tall popup — an ISD intersection — would push the deficiency out of view) and is
    // held to the room above the deficiency, scrolling when it needs more.
    const open = () => {
      if (!target || !target.openPopup || !target.getPopup || !target.getPopup()) return;
      const popup = target.getPopup();
      const keep = { autoPan: popup.options.autoPan, maxHeight: popup.options.maxHeight };
      popup.options.autoPan = false;
      try {
        target.openPopup(box && target.getBounds ? [box[1][0], (box[0][1] + box[1][1]) / 2] : undefined);
      } catch (e) { return; }
      const topPx = map.latLngToContainerPoint(box ? [box[1][0], focus.lng] : [focus.lat, focus.lng]).y;
      const room = Math.max(120, Math.round(topPx - 48));
      const hold = () => {
        const el = popup.getElement();
        if (el && el.offsetHeight > room) {
          popup.options.maxHeight = room;
          popup.update();
        }
      };
      setTimeout(hold, 30);            // after the popup sizer has had its say
      map.once('popupclose', () => { popup.options.autoPan = keep.autoPan; popup.options.maxHeight = keep.maxHeight; });
    };
    if (box) {
      // a deficiency with an extent: all of it in the lower part of the map
      map.flyToBounds(box, { paddingTopLeft: [40, Math.round(size.y * 0.42)], paddingBottomRight: [40, 30],
        maxZoom: Math.max(focus.zoom || 15, 17), duration: 0.8 });
    } else {
      // a point: below the middle of the map
      const zoom = Math.max(map.getZoom(), focus.zoom || 15);
      const center = map.unproject(map.project([focus.lat, focus.lng], zoom).subtract([0, size.y * 0.22]), zoom);
      map.flyTo(center, zoom, { duration: 0.8 });
    }
    map.once('moveend', open);
    return () => map.off('moveend', open);
  }, [map, focus, refs]);
  return null;
}

/** Double-click = add a Point of Interest (when the page keeps them). */
function DblClickCatcher({ onAdd }) {
  useMapEvents({ dblclick: (e) => { if (onAdd) onAdd({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return null;
}
const PLACE_ICON = L.divIcon({ className: 'so-place-icon', html: '<span>★</span>', iconSize: [22, 22], iconAnchor: [11, 11] });

function PlacePopup({ place, index, onUpdate, onRemove }) {
  const [note, setNote] = useState(place.note || '');
  const [saved, setSaved] = useState(false);
  useEffect(() => { setNote(place.note || ''); }, [place.note]);
  useEffect(() => { if (!saved) return undefined; const t = setTimeout(() => setSaved(false), 2500); return () => clearTimeout(t); }, [saved]);
  const save = () => { onUpdate(place.id, note); setSaved(true); };
  return (
    <div className="safety-popup so-place-popup">
      <strong>Point of Interest {index + 1}</strong>
      <div className="safety-popup-mp">{place.lat.toFixed(5)}, {place.lng.toFixed(5)}{place.created ? ` · ${place.created.slice(0, 10)}` : ''}</div>
      <textarea aria-label="Note" rows={3} placeholder="Add a note" value={note}
        onChange={(e) => setNote(e.target.value)} onBlur={() => note !== (place.note || '') && save()} />
      <div className="safety-popup-actions">
        <button type="button" className="safety-btn" onClick={save}>Save note</button>
        <button type="button" className="safety-btn safety-btn-ghost" onClick={() => onRemove(place.id)}>Remove this Point of Interest</button>
        {saved && <span className="so-note-saved" role="status">✓ Note saved</span>}
      </div>
    </div>
  );
}

/** Rays of each maneuver of one intersection, switched on and off here (ISD view). */
function RaySwitches({ maneuvers, isd }) {
  const ids = (maneuvers || []).map((m) => m.id).filter(Boolean);
  if (!isd || !ids.length) return null;
  const hidden = new Set(isd.hidden);
  return (
    <div className="so-rays">
      <div className="so-rays-head">
        <strong>Sight rays on the map</strong>
        <button type="button" className="so-link" onClick={() => isd.setMany(ids, false)}>all</button>
        <button type="button" className="so-link" onClick={() => isd.setMany(ids, true)}>none</button>
      </div>
      {/* side by side, as many on a line as fit */}
      <div className="so-rays-list">
        {maneuvers.filter((m) => m.id).map((m) => (
          <span key={m.id} className="so-rays-item">
            <label>
              <input type="checkbox" checked={!hidden.has(m.id)} onChange={() => isd.toggle(m.id)} />
              <span>{m.name || m.id}{m.quality != null ? ` · ${Math.round(m.quality * 100)}%` : ''}</span>
            </label>
            <button type="button" className="so-link" title="Show the rays of this maneuver only"
              onClick={() => isd.only(ids, m.id)}>only</button>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Every crash of a crash cluster: the full list, and a ‹ › stepper through the crashes that
 *  shows each one's complete record (the same body as the crash's own popup). */
function ClusterCrashes({ members, analysis, recommendations }) {
  const [idx, setIdx] = useState(-1);
  const map = useMap();
  // the popup keeps its first size unless told: re-fit it (and let PopupSizer widen it) per step
  useEffect(() => { const p = map && map._popup; if (p) { p.options._widened = false; p.update(); map.fire('popupopen', { popup: p }); } }, [idx, map]);
  if (!members.length) return null;
  const step = (d) => setIdx((i) => (i < 0 ? (d > 0 ? 0 : members.length - 1) : (i + d + members.length) % members.length));
  const cur = idx >= 0 ? members[Math.min(idx, members.length - 1)] : null;
  return (
    <div className="so-cluster-crashes-box">
      <div className="so-cluster-step" role="group" aria-label="Crashes of this cluster">
        <button type="button" aria-label="Previous crash" onClick={() => step(-1)}>‹</button>
        <span>{cur ? `Crash ${idx + 1} of ${members.length}` : `${members.length} crashes · step through them`}</span>
        <button type="button" aria-label="Next crash" onClick={() => step(1)}>›</button>
        {cur && <button type="button" className="so-link" onClick={() => setIdx(-1)}>list</button>}
      </div>
      {cur ? (
        <div className="so-cluster-crash"><CrashPopupBody crash={cur} analysis={analysis} recommendations={recommendations} /></div>
      ) : (
        <ul className="so-cluster-crashes">
          {members.map((m, i) => (
            <li key={m.id}>
              <button type="button" className="so-link" onClick={() => setIdx(i)}>
                MP {fmtMp(m.milepost)} · {m.severity || m.severityClass} · {m.manner || 'crash'} · {m.datetime ? m.datetime.slice(0, 10) : ''}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// rank badges of the map's Top N (one icon per label and colour, built once): a site's badge is on
// its analysis' colour — "SSD 2" when ranks are kept per analysis, "#2" when combined
const rankIcons = {};
const rankIcon = (label, kind, pinned, color = null) => {
  const k = `${kind}|${label}|${pinned ? 1 : 0}|${color || ''}`;
  if (!rankIcons[k]) {
    const dark = color && color.toLowerCase() === ANALYSIS_COLORS.GLR.toLowerCase();
    rankIcons[k] = L.divIcon({ className: `so-rank-badge so-rank-${kind}${dark ? ' so-rank-dark' : ''}`, iconSize: null, iconAnchor: [-8, 30],
      html: `<span${color ? ` style="background:${color}"` : ''}>${pinned ? '📌 ' : ''}${label}</span>` });
  }
  return rankIcons[k];
};
/** A site's badge label: its analysis and rank within it, or its combined rank. */
const siteBadge = (nav, s) => {
  const r = nav.rank.get(s.id);
  if (r == null) return '—';
  return nav.rankBy === 'analysis' ? `${s.analysis} ${r}` : `#${r}`;
};
const TOP_OPEN_KEY = 'safetyOverview:topOnMapOpen';
/** Milepost labels along the roads shown: every 5, 2, 1 or 0.5 miles by the length shown, on one direction of each road. */
const mpStep = (span) => (span > 25 ? 5 : span > 8 ? 2 : span > 3 ? 1 : 0.5);
export function milepostTicks(segments, direction, pathKeys) {
  const out = [];
  segments.forEach((s) => {
    const dirs = Object.entries(s.directions || {}).filter(([d]) => (!direction || d === direction) && (!pathKeys || pathKeys.has(`${s.key}|${d}`)));
    // the direction with mileposts: a few surveys have none stored for one direction
    const pts = dirs.map(([, line]) => (line.points || []).filter((p) => p[0] != null && p[2] != null))
      .sort((a, b) => b.length - a.length)[0] || [];
    if (pts.length < 2) return;
    const mps = pts.map((p) => p[2]);
    const lo = Math.min(...mps);
    const hi = Math.max(...mps);
    const span = hi - lo;
    const step = mpStep(span);
    for (let mp = Math.ceil(lo / step) * step; mp <= hi + 1e-9; mp += step) {
      const near = pts.reduce((best, p) => (Math.abs(p[2] - mp) < Math.abs(best[2] - mp) ? p : best), pts[0]);
      if (Math.abs(near[2] - mp) < step / 2) out.push({ key: `${s.key}|${mp}`, mp, lat: near[0], lng: near[1] });
    }
  });
  return out;
}

/** Where the road names stand: one in the middle of each segment, or (repeat, close up) one every other milepost step. */
export function roadNameLabels(segments, direction, pathKeys, repeat = true) {
  const out = [];
  segments.forEach((s) => {
    const pts = Object.entries(s.directions || {}).filter(([d]) => (!direction || d === direction) && (!pathKeys || pathKeys.has(`${s.key}|${d}`)))
      .map(([, line]) => (line.points || []).filter((p) => p[0] != null)).sort((a, b) => b.length - a.length)[0] || [];
    if (pts.length < 2) return;
    const withMp = pts.filter((p) => p[2] != null);
    const mps = withMp.map((p) => p[2]);
    const lo = Math.min(...mps);
    const span = Math.max(...mps) - lo;
    if (!repeat || withMp.length < 2 || span <= 0) {
      const mid = pts[Math.floor(pts.length / 2)];
      out.push({ key: `${s.key}|mid`, road: s.road, lat: mid[0], lng: mid[1] });
      return;
    }
    const count = Math.max(1, Math.floor(span / (mpStep(span) * 2)));
    for (let i = 0; i < count; i += 1) {
      const mp = lo + ((i + 0.5) * span) / count;
      const near = withMp.reduce((best, p) => (Math.abs(p[2] - mp) < Math.abs(best[2] - mp) ? p : best), withMp[0]);
      out.push({ key: `${s.key}|${i}`, road: s.road, lat: near[0], lng: near[1] });
    }
  });
  return out;
}

// closer than the tiles go (meeting 2026-10-26): past a layer's own zoom Leaflet enlarges its last tiles
const MAX_ZOOM = 24;
const HOTSPOT_COLOR = '#f59e0b';          // hotspot bands (New project spawning)
// a site's note on the map: a small pen in its status colour, left of the site
const noteIcons = {};
const noteIcon = (status) => {
  if (!noteIcons[status]) {
    noteIcons[status] = L.divIcon({ className: 'so-note-marker', iconSize: [18, 18], iconAnchor: [24, 9],
      html: `<span style="background:${STATUS_COLOR[status] || '#64748b'}" title="${STATUS_LABEL[status] || ''}">✎</span>` });
  }
  return noteIcons[status];
};
const HOTSPOT_BADGE = '#b45309';

/** Top on map (Safety Overview): how many of the top-ranked sites (and, in the RDV mode, clusters) the
 *  map shows, and a Prev / Next through them (pinned rows too); the crash-cluster stepper lives here as
 *  well. The ranks are the tables' # and the insights' order, so the map's head is the panels' head.
 *  Ranked per analysis (nav.sites.rankBy === 'analysis'), the Top N is per analysis and Prev / Next
 *  walks one analysis at a time — its chip picks which. */
function TopOnMap({ nav, crash, onGo }) {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(TOP_OPEN_KEY) !== '0'; } catch (e) { return true; } });
  const [idx, setIdx] = useState({ sites: -1, clusters: -1, hotspots: -1 });
  const [pick, setPick] = useState(null);                 // the analysis Prev / Next walks (per-analysis ranks)
  const toggle = (v) => { setOpen(v); try { localStorage.setItem(TOP_OPEN_KEY, v ? '1' : '0'); } catch (e) { /* private window */ } };
  // New project spawning: the hotspots row stands where the sites row is
  const sitesNav = nav.sites || { list: [], rank: new Map(), rankBy: 'combined', analyses: [], pinned: [] };
  const perAnalysis = Boolean(nav.sites) && sitesNav.rankBy === 'analysis';
  const groups = useMemo(() => {
    const g = {};
    sitesNav.list.filter((x) => x.lat != null).forEach((x) => { (g[x.analysis] = g[x.analysis] || []).push(x); });
    Object.values(g).forEach((l) => l.sort((a, b) => (sitesNav.rank.get(a.id) ?? 1e9) - (sitesNav.rank.get(b.id) ?? 1e9)));
    const order = [...(sitesNav.analyses || []), ...Object.keys(g).filter((a) => !(sitesNav.analyses || []).includes(a))];
    return order.filter((a) => g[a]).map((a) => [a, g[a]]);
  }, [sitesNav.list, sitesNav.rank, sitesNav.analyses]);
  const picked = perAnalysis ? (groups.find(([a]) => a === pick) || groups[0] || [null, []]) : null;
  const clustersNav = nav.clusters || null;
  const hotNav = nav.hotspots || null;
  useEffect(() => { setIdx({ sites: -1, clusters: -1, hotspots: -1 }); },
    [sitesNav.list.length, clustersNav ? clustersNav.list.length : 0, sitesNav.value, clustersNav ? clustersNav.value : 0, sitesNav.rankBy, pick,
      hotNav ? hotNav.list.length : 0, hotNav ? hotNav.value : 0]);
  const listOf = (kind) => (kind === 'sites' && perAnalysis ? picked[1] : nav[kind].list.filter((x) => x.lat != null));
  const step = (kind, d) => {
    const list = listOf(kind);
    if (!list.length) return;
    const cur = idx[kind];
    const i = cur < 0 ? (d > 0 ? 0 : list.length - 1) : (cur + d + list.length) % list.length;
    setIdx((x) => ({ ...x, [kind]: i }));
    onGo(list[i], kind === 'clusters' || kind === 'hotspots' ? 15 : 16);
  };
  if (!open) {
    return <button type="button" className="so-top-chip" onClick={() => toggle(true)} title="Top sites on the map">Top on map ▾</button>;
  }
  const count = (kind) => {
    const n = nav[kind];
    const list = listOf(kind);
    const cur = idx[kind] >= 0 ? list[idx[kind]] : null;
    if (!cur) return `${list.length} on map`;
    const label = kind === 'sites' ? siteBadge(n, cur) : kind === 'hotspots' ? `${n.prefix || 'H'}${n.rank.get(cur.id) ?? '—'}` : `#${n.rank.get(cur.id) ?? '—'}`;
    return `${label}${n.pinned.includes(cur.id) ? ' 📌' : ''} · ${idx[kind] + 1} of ${list.length}`;
  };
  const stepper = (kind, label) => (
    <>
      <button type="button" aria-label={`Previous ${label}`} onClick={() => step(kind, -1)}>‹</button>
      <span className="so-top-count">{count(kind)}</span>
      <button type="button" aria-label={`Next ${label}`} onClick={() => step(kind, 1)}>›</button>
    </>
  );
  return (
    <div className={`so-top-panel ${perAnalysis ? 'so-top-per' : ''}`} role="group" aria-label="Top on map">
      <div className="so-top-head">
        <span>Top on map</span>
        {nav.sites && sitesNav.onRankBy && (
          <span className="so-top-rankby" role="group" aria-label="Rank"
            title="By analysis: each analysis is ranked on its own and the Top N is per analysis. Combined: one ranking across the analyses.">
            <span>Rank</span>
            <button type="button" aria-pressed={perAnalysis} onClick={() => sitesNav.onRankBy('analysis')}>By analysis</button>
            <button type="button" aria-pressed={!perAnalysis} onClick={() => sitesNav.onRankBy('combined')}>Combined</button>
          </span>
        )}
        <button type="button" className="lg-close" onClick={() => toggle(false)} title="Minimize">–</button>
      </div>
      {nav.sites && (
      <div className="so-top-row" title={sitesNav.title}>
        <span className="so-top-label">{perAnalysis ? 'Sites per analysis' : 'Sites'}</span>
        <TopNSelect value={sitesNav.value} onChange={sitesNav.onChange} max={sitesNav.max} presets={sitesNav.presets} className="" />
        {perAnalysis ? <><span /><span /><span /></> : stepper('sites', 'site')}
      </div>
      )}
      {hotNav && (
        <div className="so-top-row" title={hotNav.title}>
          <span className="so-top-label">{hotNav.label || 'Hotspots'}</span>
          <TopNSelect value={hotNav.value} onChange={hotNav.onChange} max={hotNav.max} presets={hotNav.presets} className="" />
          {stepper('hotspots', 'hotspot')}
        </div>
      )}
      {perAnalysis && groups.length > 0 && (
        <div className="so-top-an-row">
          <span className="so-top-chips">
            {groups.map(([a, l]) => (
              <button key={a} type="button" className={`so-top-an ${a === picked[0] ? 'on' : ''} ${a === 'GLR' ? 'dark' : ''}`}
                aria-pressed={a === picked[0]}
                style={a === picked[0] ? { background: ANALYSIS_COLORS[a], borderColor: ANALYSIS_COLORS[a] } : { borderColor: ANALYSIS_COLORS[a], color: ANALYSIS_COLORS[a] }}
                onClick={() => setPick(a)} title={`${ANALYSIS_NAMES[a]} · ${l.length} on the map`}>{a}</button>
            ))}
          </span>
          <span className="so-top-step">{stepper('sites', `${picked[0]} site`)}</span>
        </div>
      )}
      {clustersNav && (
        <div className="so-top-row" title={clustersNav.title}>
          <span className="so-top-label">Clusters</span>
          <TopNSelect value={clustersNav.value} onChange={clustersNav.onChange} max={clustersNav.max} className="" />
          {stepper('clusters', 'cluster')}
        </div>
      )}
      {crash.list.length > 0 && (
        <div className="so-top-row">
          <span className="so-top-label">Crash clusters</span>
          <span />
          <button type="button" aria-label="Previous crash cluster" onClick={() => crash.go(-1)}>‹</button>
          <span className="so-top-count">{crash.idx < 0 ? `${crash.list.length} places` : `${crash.idx + 1} of ${crash.list.length}`}</span>
          <button type="button" aria-label="Next crash cluster" onClick={() => crash.go(1)}>›</button>
        </div>
      )}
    </div>
  );
}

/** A popup taller than 75% of the map gets wider (and, when still too tall, scrolls). */
function PopupSizer() {
  const map = useMapEvents({
    popupopen: (e) => {
      const popup = e.popup;
      const fit = () => {
        const el = popup.getElement();
        if (!el) return;
        const size = map.getSize();
        if (el.offsetHeight > 0.75 * size.y && !popup.options._widened) {
          popup.options._widened = true;
          popup.options.maxWidth = Math.min(size.x * 0.85, 760);
          popup.options.minWidth = Math.min(size.x * 0.55, 520);
          popup.options.maxHeight = Math.round(size.y * 0.85);
          popup.update();
        }
      };
      setTimeout(fit, 0);
    },
    popupclose: (e) => { if (e.popup && e.popup.options) delete e.popup.options._widened; },
  });
  return null;
}

// the layer-control names of the base maps -> the keys the PDF export understands
const BASE_LAYER_KEYS = { 'Street (OSM)': 'street', 'Satellite (Esri)': 'satellite', 'Terrain (OpenTopoMap)': 'terrain',
  'Light gray (Esri)': 'lightgray', 'Street map': 'plain' };

/** Reports the base map the user picked in the layer control (the PDF draws the same one). */
// the right-of-way layer: off until the user turns it on in the layer control (its data is fetched then)
export const ROW_LAYER = 'Right of way (approximate)';
const ROW_COLOR = '#9333ea';          // purple (Yaron, 2026-10-25)
const escHtml = (t) => String(t == null ? '' : t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
function OverlayWatcher({ onRow, onLayer }) {
  // Leaflet fires overlayadd for every layer that STARTS checked, so the first
  // moments after mount are the map setting itself up, not the user choosing
  // anything. onRow is outside that guard on purpose: it drives the right-of-way
  // data load and must fire immediately.
  const ready = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => { ready.current = true; }, 1500);
    return () => clearTimeout(t);
  }, []);
  useMapEvents({
    overlayadd: (e) => {
      if (onRow && e.name === ROW_LAYER) onRow(true);
      if (onLayer && ready.current) onLayer(e.name, true);
    },
    overlayremove: (e) => {
      if (onRow && e.name === ROW_LAYER) onRow(false);
      if (onLayer && ready.current) onLayer(e.name, false);
    },
  });
  return null;
}

/** Street View picking: the first click is the spot to stand on, the second the direction to look. */
function StreetViewPicker({ step, point, onPoint, onDirection }) {
  useMapEvents({
    click: (e) => {
      if (step === 1) onPoint(e.latlng);
      else if (step === 2 && point) onDirection(e.latlng);
    },
  });
  return null;
}
const bearing = (a, b) => {
  const r = Math.PI / 180;
  const y = Math.sin((b.lng - a.lng) * r) * Math.cos(b.lat * r);
  const x = Math.cos(a.lat * r) * Math.sin(b.lat * r) - Math.sin(a.lat * r) * Math.cos(b.lat * r) * Math.cos((b.lng - a.lng) * r);
  return (Math.atan2(y, x) / r + 360) % 360;
};
const SV_ICON = L.divIcon({ className: 'so-sv-marker', iconSize: [22, 22], iconAnchor: [11, 11], html: '<span>👁</span>' });

function BaseLayerWatcher({ onChange }) {
  useMapEvents({ baselayerchange: (e) => { if (onChange) onChange(BASE_LAYER_KEYS[e.name] || 'street'); } });
  return null;
}

function ZoomWatcher({ onZoom }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => { onZoom(map.getZoom()); }, [map, onZoom]);
  return null;
}

/** Reports zoom and pan to the page (telemetry; also the geographic heatmap's
 *  input later). Separate from ZoomWatcher, which feeds the map's own state —
 *  this one is a pure out-channel and fires only on a user-driven end event. */
function ViewportWatcher({ onViewport }) {
  const map = useMapEvents({
    zoomend: () => onViewport({ kind: 'zoom', zoom: map.getZoom(), center: map.getCenter() }),
    moveend: () => onViewport({ kind: 'pan', zoom: map.getZoom(), center: map.getCenter() }),
  });
  return null;
}

/** A legend swatch drawn hollow: the deficient stations too short to count. */
const HollowSwatch = ({ shape = 'dot', label }) => (
  <div className="lg-row"><i className={`${SHAPE_CLASS[shape] || 'lg-dot'} ov-lg-hollow`} style={{ borderColor: DETAIL_COLORS.bad }} />{label}</div>
);

const SHAPE_CLASS = { dot: 'lg-dot', circle: 'lg-dot', square: 'lg-dot ov-lg-square', diamond: 'lg-dot ov-lg-diamond' };
const Swatch = ({ color, label, shape = 'line' }) => (
  <div className="lg-row"><i className={SHAPE_CLASS[shape] || 'lg-line'} style={{ background: color }} />{label}</div>
);

/** What the single-analysis view draws, in that analysis' own report colours. */
export const ISD_CASES = [
  ['B1', 'left turn from the minor road'], ['B2', 'right turn from the minor road'],
  ['B3', 'crossing from the minor road'], ['F', 'left turn from the major road'],
];

function DetailLegend({ analysis, isd, grwShield = false }) {
  const shape = SHAPES[analysis] || 'dot';
  if (analysis === 'SSD' || analysis === 'PSD') {
    return (<><Swatch shape={shape} color={DETAIL_COLORS.bad} label={analysis === 'SSD' ? 'Short of stopping sight distance, in a deficiency run' : 'Unsafe pass, in a deficiency run'} />
      <HollowSwatch shape={shape} label="Too short to count (below the minimum run)" />
      <Swatch shape={shape} color={DETAIL_COLORS.ok} label="Station passes" /></>);
  }
  if (analysis === 'GRW') {
    return (<><Swatch shape={shape} color={DETAIL_COLORS.bad} label={grwShield ? 'Warranted, no guardrail (deficiency)' : 'Guardrail warranted (YES)'} />
      <HollowSwatch shape={shape} label="Warranted, too short to count (below the minimum run)" />
      {grwShield && <Swatch shape={shape} color={DETAIL_COLORS.shielded} label="Warranted, existing guardrail (shielded)" />}
      <Swatch shape={shape} color={DETAIL_COLORS.ok} label="Not warranted (NO)" />
      {grwShield && <div className="lg-row"><i className="lg-line" style={{ background: GUARDRAIL_COLOR }} />Existing guardrail</div>}</>);
  }
  if (analysis === 'GLR') return <>{GLR_BANDS.map(([label, color]) => <Swatch key={label} shape="dot" color={color} label={`${label} glare / yr`} />)}</>;
  if (analysis === 'FSA') {
    return (<><div className="lg-row"><i className="ov-lg-tri" style={{ borderBottomColor: DETAIL_COLORS.flat }} />Flat spot, pointing downhill</div>
      <div className="lg-row"><i className="ov-lg-tri" style={{ borderBottomColor: DETAIL_COLORS.slope }} />Station gradient direction</div></>);
  }
  if (analysis === 'ISD') {
    return (<><Swatch color={DETAIL_COLORS.bad} label="Sight ray: target not visible" />
      <Swatch color={DETAIL_COLORS.ok} label="Sight ray: target visible" />
      {isd && isd.hidden.length > 0 && (
        <div className="lg-row">
          <button type="button" className="so-link" onClick={isd.reset}>Show every ray ({isd.hidden.length} maneuver{isd.hidden.length === 1 ? '' : 's'} hidden)</button>
        </div>
      )}</>);
  }
  return Object.entries(SEVERITY_COLORS).map(([s, c]) => <Swatch key={s} color={c} label={`Curve, ${s}`} />);
}

function Legend({ colourBy, analyses, roads, details, showIntersections, showPaths, showCrashes, isd, showNetwork, grwShield = false, showClusters = true }) {
  const detail = details.length > 0;
  const [open, setOpen] = React.useState(true);
  if (!open) {
    return <button type="button" className="safety-map-legend safety-legend-min" onClick={() => setOpen(true)}>Legend</button>;
  }
  return (
    <div className="safety-map-legend">
      <div className="lg-head">
        <span className="lg-title lg-title-first">{detail ? `${details[0]} · ${ANALYSIS_NAMES[details[0]]}` : colourBy === 'severity' ? 'Sites by severity' : 'Sites by analysis'}</span>
        <button type="button" className="lg-close" onClick={() => setOpen(false)} title="Minimize legend">–</button>
      </div>
      {detail ? (
        <>
          {details.map((a, i) => (
            <React.Fragment key={a}>
              {i > 0 && <div className="lg-title">{a} · {ANALYSIS_NAMES[a]}</div>}
              <DetailLegend analysis={a} isd={isd} grwShield={grwShield} />
            </React.Fragment>
          ))}
          {details.some((a) => DETAIL_ANALYSES.includes(a)) && (
            <>
              <div className="lg-title">Deficiency runs (halo, follows the severity filter)</div>
              {Object.entries(SEVERITY_COLORS).map(([s, c]) => (
                <div className="lg-row" key={s}><i className="lg-line ov-lg-halo" style={{ background: c }} />{s}</div>))}
            </>
          )}
          {showPaths && <Swatch color={PATH_COLOR} label="Analysis path" />}
        </>
      ) : colourBy === 'severity'
        ? Object.entries(SEVERITY_COLORS).map(([s, c]) => (
          <div className="lg-row" key={s}><i className="lg-line" style={{ background: c }} />{s}</div>))
        : analyses.map((a) => (
          <div className="lg-row" key={a} title={ANALYSIS_NAMES[a]}><i className="lg-line" style={{ background: ANALYSIS_COLORS[a] }} />{a}</div>))}
      {showClusters && analyses.length > 1 && <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#f8fafc', borderStyle: 'dashed' }} />Multi-deficiency cluster</div>}
      {showIntersections && <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: ANALYSIS_COLORS.ISD }} />Intersection</div>}
      {showCrashes && (
        <>
          <div className="lg-title">Crashes</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.fatal }} />Fatal</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.injury }} />Injury</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.pdo }} />Property damage</div>
          <div className="lg-row"><i className="lg-dot lg-hollow" />Animal strike</div>
          <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#e8a30c' }} />Glare-time match</div>
          <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#6b2e8a' }} />Crash cluster</div>
        </>
      )}
      {showNetwork && (
        <>
          <div className="lg-title">Road network (SOP), taken from</div>
          {NET_SOURCES.map(([k, label]) => (
            <div className="lg-row" key={k}><i className="lg-dot lg-ring" style={{ borderColor: NET_SOURCE_COLORS[k] }} />{label}</div>
          ))}
          <div className="lg-row ov-legend-mp" title="Road features the passing-zone rules use">
            {NET_CONDITIONS.map(([k, label]) => `${NET_GLYPH[k]} ${label.split(' ')[0].toLowerCase()}`).join(' · ')}
          </div>
        </>
      )}
      <div className="lg-title">Roads</div>
      {roads.map((r) => (
        <div className="lg-row" key={r.road}>
          <i className="lg-line" style={{ background: detail ? PATH_COLOR : r.color }} />
          {r.road}<span className="ov-legend-mp"> · {r.ranges.map(([a, b]) => `MP ${a.toFixed(1)}–${b.toFixed(1)}`).join(' · ')}</span>
        </div>
      ))}
    </div>
  );
}

const ReportButton = ({ url, label }) => (url ? (
  <a className="safety-btn safety-report-btn" data-track="export.site_report" data-track-kind="export" href={url} target="_blank" rel="noreferrer">{label} ↗</a>
) : null);

export default function OverviewMap({ segments, sites, clusters, intersections = [], analyses, colourBy, roadColors, focus,
  details = [], stationLayers = null, showPaths = true, direction = '',
  crashes = null, crashClusters = [], crashAnalysis = 'ALL', recommendations = [], isd = null,
  places = null, onAddPlace = null, onUpdatePlace = null, onRemovePlace = null, network = null, onBaseLayer = null, onViewport = null, onLayer = null,
  canDeleteNetwork = null, onDeleteNetwork = null, grwShield = false, topNav = null, defaultBase = 'street',
  hotspots = [], fadedSites = [], notes = null, onNote = null, row = null, onRowToggle = null, rowFlags = null,
  pathsInControl = false, pathKeys = null, stationsOn = true, showClusters = true, loadingText = null }) {
  // pathsInControl (customer modes): the analysis paths are a layer of the layer control (on when showPaths);
  // pathKeys: Set of `${segKey}|${dir}` — only those paths (the roads of the sites shown); stationsOn: the
  // station layer starts on, or a list: one layer per analysis, these start on; showClusters: false = no multi-deficiency clusters anywhere (layer, legend, hint)
  // rowFlags: {site id: {pct}} — how much of its most exposed blocked sight line lies inside the right of way
  // row (right of way): {features, source, error, loading} — offered as a layer, off by default; onRowToggle(on) when switched
  // notes: {site id: note} (site notes, 2026-10-25); onNote(site) opens its note for editing
  // hotspots (New project spawning): [{id, rank, coords, lat, lng, n, analyses, mpStart, mpEnd, road}] drawn as amber bands
  // fadedSites (rehab): the segment's sites outside the project limits, drawn faded for context
  // defaultBase: the base layer the map opens on ('street' | 'satellite' | 'terrain' | 'lightgray')
  // topNav (Safety Overview): the Top on map control — {sites, clusters: {value, onChange, max, rank, list, pinned, title}}
  // grwShield: existing guardrails shield GRW warrants (the page's rule) — the GRW legend says so
  // canDeleteNetwork(src) -> may this user delete a road element of that source; onDeleteNetwork(feature) asks to
  // network: the road-network features the PSD rules used ({type, mp, lat, lng, label, src, segName})
  // places: Points of Interest (double-click the map to add one); null = the feature is off
  // isd: {cases, hidden, onToggle} — the maneuver cases present in the ISD rays and the ones switched off
  // crashes: null = no crash data (the Project Overview); an array = the Safety Overview's
  // filtered crashes ({...crash, id: 'crash:<segKey>:<id>', lng}) and crashClusters their clusters.
  // details: the selected analyses when there are at most three (else []). Their stations are
  // drawn in each report's own colours on a light purple path; those analyses' deficiency runs
  // stay clickable but invisible, except HCA whose runs ARE the curves (severity colours).
  // Intersections are an ISD subject: shown only while ISD is among the selected analyses.
  const detail = details.length > 0;
  // every shown segment of a demo state -> that state's fictional basemap
  const basemap = segments.length && segments.every((s) => s.basemap && s.basemap === segments[0].basemap) ? segments[0].basemap : null;
  // the analyses drawn station by station: the selected detail analyses, and any layer the page sends (the customer modes' ISD rays)
  const withStations = new Set([...details.filter((a) => DETAIL_ANALYSES.includes(a)), ...(stationLayers || []).map((l) => l.analysis)]);
  // one analysis for a moment, the others hidden ON THE MAP ALONE (the ranking, tables and panels are untouched): hold
  // Shift+Space on a site (hovered, its popup open, or selected), or the map's button. The Analyses list is the lasting filter.
  const [onlyHold, setOnlyHold] = useState(null);     // while held: the analysis, or '' = no site to take it from
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [onlyPick, setOnlyPick] = useState(null);
  const onlyTarget = useRef({ hover: null, popup: null, selected: null });
  const mapAnalyses = useMemo(() => analyses.filter((a) => sites.some((s) => s.analysis === a)), [analyses, sites]);
  const only = onlyHold || (onlyPick && mapAnalyses.includes(onlyPick) ? onlyPick : null);
  const shownSites = useMemo(() => (only ? sites.filter((s) => s.analysis === only) : sites), [sites, only]);
  const stationsName = `${Array.from(withStations).join(' · ')} stations`;
  const stationGroups = useMemo(() => {
    const kept = (l) => !only || l.analysis === only;
    return Array.isArray(stationsOn)
      ? (stationLayers || []).map((l) => ({ name: `${l.analysis} stations`, on: stationsOn.includes(l.analysis), layers: kept(l) ? [l] : [] }))
      : [{ name: stationsName, on: stationsOn, layers: (stationLayers || []).filter(kept) }];
  }, [stationLayers, stationsOn, stationsName, only]);
  const showIntersections = analyses.includes('ISD');
  const refs = useRef({});
  const [zoom, setZoom] = useState(null);
  // hold Space (outside a text field) or the middle mouse button over the map: the background map alone
  const [peek, setPeek] = useState(false);
  useEffect(() => {
    const typing = (el) => el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
    const down = (e) => {
      if (e.code !== 'Space' || typing(e.target) || typing(document.activeElement)) return;
      e.preventDefault();                          // no page scroll, no click on a focused button
      if (document.activeElement && document.activeElement.tagName === 'BUTTON') document.activeElement.blur();
      if (e.repeat) return;
      if (e.shiftKey) {
        const t = onlyTarget.current;
        setOnlyHold(t.hover || t.popup || t.selected || '');
      } else setPeek(true);
    };
    const up = (e) => { if (e.code === 'Space') { setPeek(false); setOnlyHold(null); } };
    const mouseUp = (e) => { if (e.button === 1) setPeek(false); };
    const off = () => { setPeek(false); setOnlyHold(null); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('mouseup', mouseUp);
    window.addEventListener('blur', off);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('mouseup', mouseUp);
      window.removeEventListener('blur', off);
    };
  }, []);
  const onMiddleDown = (e) => { if (e.button === 1) { e.preventDefault(); setPeek(true); } };   // and no browser autoscroll
  // Google Street View (2026-10-25): 0 off · 1 waiting for the spot · 2 waiting for the direction
  const [svStep, setSvStep] = useState(0);
  const [svPoint, setSvPoint] = useState(null);
  useEffect(() => {
    if (!svStep) return undefined;
    const esc = (e) => { if (e.key === 'Escape') { setSvStep(0); setSvPoint(null); } };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [svStep]);
  const openStreetView = (to) => {
    const h = Math.round(bearing(svPoint, to));
    const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${svPoint.lat.toFixed(6)},${svPoint.lng.toFixed(6)}&heading=${h}&pitch=0&fov=90`;
    window.open(url, '_blank', 'noopener');
    setSvStep(0);
    setSvPoint(null);
  };
  // crash-cluster navigator: step through the clusters on the map (their popups carry the data)
  const [clusterIdx, setClusterIdx] = useState(-1);
  const [navFocus, setNavFocus] = useState(null);
  const navClusters = useMemo(() => (crashes ? crashClusters.filter((c) => c.lat != null) : []), [crashes, crashClusters]);
  useEffect(() => { setClusterIdx(-1); }, [navClusters.length]);
  const [placeIdx, setPlaceIdx] = useState(-1);
  const goPlace = (step) => {
    if (!places || !places.length) return;
    const i = placeIdx < 0 ? (step > 0 ? 0 : places.length - 1) : (placeIdx + step + places.length) % places.length;
    setPlaceIdx(i);
    const p = places[i];
    setNavFocus({ id: p.id, lat: p.lat, lng: p.lng, zoom: 16, stamp: Date.now() });
  };
  useEffect(() => { if (places && placeIdx >= places.length) setPlaceIdx(places.length - 1); }, [places, placeIdx]);
  const goCluster = (step) => {
    if (!navClusters.length) return;
    const i = clusterIdx < 0 ? (step > 0 ? 0 : navClusters.length - 1) : (clusterIdx + step + navClusters.length) % navClusters.length;
    setClusterIdx(i);
    const c = navClusters[i];
    setNavFocus({ id: c.id, lat: c.lat, lng: c.lng, zoom: 15, stamp: Date.now() });
  };
  const shownFocus = navFocus && (!focus || navFocus.stamp > (focus.stamp || 0)) ? navFocus : focus;
  onlyTarget.current.selected = (shownFocus && sites.find((s) => s.id === shownFocus.id)?.analysis) || null;
  const onlyEvents = useRef({});
  const siteEvents = (a) => {
    if (!onlyEvents.current[a]) {
      onlyEvents.current[a] = {
        mouseover: () => { onlyTarget.current.hover = a; },
        mouseout: () => { onlyTarget.current.hover = null; },
        popupopen: (e) => { popupAboveLine.popupopen(e); onlyTarget.current.popup = a; },
        popupclose: () => { onlyTarget.current.popup = null; },
      };
    }
    return onlyEvents.current[a];
  };
  // Cluster rings are a close-up aid: they show only from zoom 13, where the colours under
  // them read clearly. Before the first zoom reading nothing is drawn (the first view of an
  // order is the whole order).
  const far = zoom == null || zoom < 13;
  const mid = zoom != null && zoom < 15;
  const stationsShown = withStations.size > 0;
  const runColourBy = colourBy;
  const colorOfRun = (s) => (detail && s.analysis === 'HCA' ? SEVERITY_COLORS[s.severity] : null);
  const bounds = useMemo(() => {
    const pts = segments.flatMap((s) => Object.values(s.directions || {}))
      .flatMap((d) => d.points.filter((p) => p[0] != null).map((p) => [p[0], p[1]]));
    return pts.length ? pts : null;
  }, [segments]);
  const segByKey = useMemo(() => new Map(segments.map((s) => [s.key, s])), [segments]);
  const roads = useMemo(() => {
    const m = new Map();
    segments.forEach((s) => {
      if (!m.has(s.road)) m.set(s.road, { road: s.road, color: roadColors[s.road], ranges: [] });
      if (s.mpMin != null) m.get(s.road).ranges.push([s.mpMin, s.mpMax]);
    });
    return Array.from(m.values()).map((r) => ({ ...r, ranges: r.ranges.sort((a, b) => a[0] - b[0]) }));
  }, [segments, roadColors]);
  // a road-network element's popup: what, where, from which source, what it does — and Delete for those who may
  const netPopup = (f) => (
    <div className="safety-popup">
      <strong>{NET_LABEL[f.type]}</strong>
      <div className="safety-popup-mp">{f.segName} · MP {fmtMp(f.mp)}{f.mpEnd != null ? `–${fmtMp(f.mpEnd)}` : ''}</div>
      <table><tbody>
        <tr><td>What</td><td>{f.label}</td></tr>
        <tr><td>Taken from</td><td><b style={{ color: NET_SOURCE_COLORS[f.src] }}>{NET_SOURCE_NAME[f.src]}</b>{f.srcLabel ? ` · ${f.srcLabel}` : ''}</td></tr>
        {f.ref ? <tr><td>Reference</td><td>{f.ref}</td></tr> : null}
        {f.type === 'guardrail'
          ? <tr><td>Effect</td><td>shields the guardrail warrant on the {f.side === 'P' ? 'positive-direction' : f.side === 'N' ? 'negative-direction' : 'both'} side{f.side === 'both' ? 's' : ''}</td></tr>
          : <tr><td>SOP rule</td><td>no passing zone {f.approachFt} ft approaching / {f.departFt} ft departing</td></tr>}
      </tbody></table>
      {f.src === 'osm' && <div className="safety-muted">© OpenStreetMap contributors</div>}
      {onDeleteNetwork && canDeleteNetwork && canDeleteNetwork(f.src) && (
        <div className="safety-popup-actions">
          <button type="button" className="safety-btn so-danger" onClick={() => onDeleteNetwork(f)}>Delete this element…</button>
        </div>
      )}
    </div>
  );
  const colorOf = (s) => colorOfRun(s) || (runColourBy === 'severity' ? SEVERITY_COLORS[s.severity] : ANALYSIS_COLORS[s.analysis]) || '#c92a2a';

  if (!bounds) return <div className="safety-map-empty">No surveyed centreline to show for this selection.</div>;

  return (
    <div className={`safety-map-holder ${peek ? 'ov-peek' : ''} ${svStep ? 'so-sv-picking' : ''}`} onMouseDown={onMiddleDown}
      onMouseLeave={() => { onlyTarget.current.hover = null; }}
      onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}>
      {peek && <div className="ov-peek-badge">Background map only · release to bring the layers back</div>}
      {loadingText && (
        <div className="so-map-loading" role="status">
          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> {loadingText}
        </div>
      )}
      {onlyHold !== null && (
        <div className="ov-peek-badge">{onlyHold ? `Only ${onlyHold} · release to bring the others back` : 'Point at a deficiency or select one, then hold Shift+Space'}</div>
      )}
      <button type="button" className={`so-sv-btn ${svStep ? 'on' : ''}`} aria-pressed={Boolean(svStep)}
        title="Google Street View: click the spot to stand on, then the direction to look — it opens in a new tab"
        onClick={() => { setSvStep(svStep ? 0 : 1); setSvPoint(null); }}>
        <i className="fa-solid fa-street-view" aria-hidden="true" />
      </button>
      {mapAnalyses.length > 1 && (
        <button type="button" className={`so-sv-btn so-only-btn ${onlyOpen ? 'on' : ''}`} aria-pressed={onlyOpen} aria-label="Show one analysis"
          title="Show one analysis and hide the others for a moment, on the map only. Or hold Shift+Space on a deficiency."
          onClick={() => { setOnlyOpen(!onlyOpen); setOnlyPick(null); }}>
          <i className="fa-solid fa-filter" aria-hidden="true" />
        </button>
      )}
      {mapAnalyses.length > 1 && onlyOpen && (
        <div className="so-only-panel" role="group" aria-label="Show one analysis">
          {mapAnalyses.map((a) => (
            <button key={a} type="button" className={onlyPick === a ? 'on' : ''} aria-pressed={onlyPick === a} title={ANALYSIS_NAMES[a]}
              onClick={() => setOnlyPick(onlyPick === a ? null : a)}>
              <i style={{ background: ANALYSIS_COLORS[a] }} aria-hidden="true" />{a}
            </button>
          ))}
        </div>
      )}
      {svStep > 0 && (
        <div className="so-sv-hint" role="status">
          {svStep === 1 ? 'Street View: click the spot to stand on' : 'Street View: click the direction to look'} · Esc to cancel
        </div>
      )}
      <MapContainer bounds={bounds} className="safety-map" scrollWheelZoom doubleClickZoom={!onAddPlace}>
        <DblClickCatcher onAdd={onAddPlace} />
        <FitBounds bounds={bounds} />
        <ZoomWatcher onZoom={setZoom} />
        {onViewport && <ViewportWatcher onViewport={onViewport} />}
        <BaseLayerWatcher onChange={onBaseLayer} />
        <OverlayWatcher onRow={onRowToggle} onLayer={onLayer} />
        <StreetViewPicker step={svStep} point={svPoint} onPoint={(ll) => { setSvPoint(ll); setSvStep(2); }} onDirection={openStreetView} />
        {svPoint && <Marker position={svPoint} icon={SV_ICON} pane="ovDecor" interactive={false} />}
        <PopupSizer />
        <FlyToFocus focus={shownFocus} refs={refs} />
        <Pane name="ovRow" style={{ zIndex: 400 }} />
        <Pane name="ovHotspots" style={{ zIndex: 405 }} />
        <Pane name="ovSites" style={{ zIndex: 410 }} />
        <Pane name="ovIntersections" style={{ zIndex: 420 }} />
        <Pane name="ovRails" style={{ zIndex: 418 }} />
        <Pane name="ovClusters" style={{ zIndex: 430 }} />
        <Pane name="ovCrashClusters" style={{ zIndex: 425 }} />
        <Pane name="ovCrashes" style={{ zIndex: 460 }} />
        <Pane name="ovDecor" style={{ zIndex: 470 }} />
        {shownFocus && shownFocus.lat != null && shownFocus.bounds && (
          // the selection: a dashed box around the whole deficiency
          <Rectangle key={shownFocus.stamp} bounds={selectionBox(shownFocus.bounds)} pane="ovDecor" interactive={false}
            pathOptions={{ color: '#ff9433', weight: 3, fillOpacity: 0.06, dashArray: '4 5' }} />
        )}
        <LayersControl position="topright">
          {/* a demo state brings its own fictional map; real layers would show the wrong place */}
          {basemap ? (
            <LayersControl.BaseLayer checked name="Street map">
              <DemoBasemap state={basemap} />
            </LayersControl.BaseLayer>
          ) : (
            <>
          <LayersControl.BaseLayer checked={defaultBase !== 'satellite' && defaultBase !== 'terrain' && defaultBase !== 'lightgray'} name="Street (OSM)">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxNativeZoom={19} maxZoom={MAX_ZOOM} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer checked={defaultBase === 'satellite'} name="Satellite (Esri)">
            <TileLayer attribution="Imagery &copy; Esri, Maxar, Earthstar Geographics"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxNativeZoom={20} maxZoom={MAX_ZOOM} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer checked={defaultBase === 'terrain'} name="Terrain (OpenTopoMap)">
            <TileLayer attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" maxNativeZoom={17} maxZoom={MAX_ZOOM} />
          </LayersControl.BaseLayer>
          {/* a clean, almost white map: the deficiencies stand out. Esri has tiles to zoom 16; closer in Leaflet scales them */}
          <LayersControl.BaseLayer checked={defaultBase === 'lightgray'} name="Light gray (Esri)">
            <TileLayer attribution="Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
              maxNativeZoom={16} maxZoom={MAX_ZOOM} />
          </LayersControl.BaseLayer>
            </>
          )}

          {(showPaths || pathsInControl) && (
          <LayersControl.Overlay checked={showPaths} name="Analysis paths">
            <LayerGroup>
              {segments.flatMap((s) => Object.entries(s.directions || {})
                .filter(([dir]) => (!direction || dir === direction) && (!pathKeys || pathKeys.has(`${s.key}|${dir}`))).map(([dir, d]) => (
                <Polyline key={`${s.key}-${dir}`} positions={d.points.filter((p) => p[0] != null).map((p) => [p[0], p[1]])}
                  pathOptions={{ color: detail ? PATH_COLOR : (roadColors[s.road] || '#7aa7e8'), weight: detail ? 4 : 3, opacity: detail ? 0.85 : 0.6 }}>
                  <Tooltip sticky>{segmentName(s)} · {dir === 'P' ? 'positive' : 'negative'} direction{s.projectId ? ` · project #${s.projectId}` : ''}</Tooltip>
                </Polyline>
              )))}
            </LayerGroup>
          </LayersControl.Overlay>
          )}

          {stationsShown && stationGroups.map((g) => (
            <LayersControl.Overlay key={g.name} checked={g.on} name={g.name}>
              <StationsLayer layers={g.layers} />
            </LayersControl.Overlay>
          ))}

          <LayersControl.Overlay checked name="Road names">
            <LayerGroup>
              {zoom != null && roadNameLabels(segments, direction, pathKeys, zoom >= 11).map((t) => (
                <Marker key={t.key} position={[t.lat, t.lng]} pane="ovDecor" interactive={false}
                  icon={L.divIcon({ className: 'so-road-tag', html: esc(t.road), iconSize: null })} />
              ))}
            </LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name="Mileposts">
            <LayerGroup>
              {zoom != null && zoom >= 11 && milepostTicks(segments, direction, pathKeys).map((t) => (
                <Marker key={t.key} position={[t.lat, t.lng]} pane="ovDecor" interactive={false}
                  icon={L.divIcon({ className: 'so-mp-tick', html: `MP ${t.mp}`, iconSize: null })} />
              ))}
            </LayerGroup>
          </LayersControl.Overlay>

          {row && !basemap && (
            <LayersControl.Overlay name={ROW_LAYER}>
              <LayerGroup>
                {(row.features || []).length > 0 && (
                  <GeoJSON key={`row-${row.key || row.features.length}`} data={{ type: 'FeatureCollection', features: row.features }}
                    pane="ovRow" style={() => ({ color: ROW_COLOR, weight: 3, opacity: 0.95, dashArray: '6 4', fillColor: ROW_COLOR, fillOpacity: 0.07 })}
                    onEachFeature={(f, layer) => {
                      const src = f.properties.source || row.source || {};
                      layer.bindTooltip(
                        `<div class="so-row-tip"><b>State highway right of way</b>${f.properties.town ? ` · ${escHtml(f.properties.town)}` : ''}`
                        + `<br/>Taken from: ${escHtml(src.label || 'the state DOT')}${src.credit ? ` (${escHtml(src.credit)})` : ''}`
                        + `<br/><span>${escHtml(src.note || 'Approximate.')}</span></div>`, { sticky: true });
                    }} />
                )}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {notes && Object.keys(notes).length > 0 && (
            <LayersControl.Overlay checked name="Site notes">
              <LayerGroup>
                {sites.filter((s) => notes[s.id] && s.lat != null).map((s) => (
                  <Marker key={`nt-${s.id}`} position={[s.lat, s.lng]} pane="ovDecor" interactive={false}
                    icon={noteIcon(notes[s.id].status)} />
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {hotspots.length > 0 && (
            <LayersControl.Overlay checked name="Hotspots">
              <LayerGroup>
                {hotspots.map((h) => {
                  const tip = (
                    <Tooltip sticky>
                      <b>{h.label || `H${h.rank}`} · {h.segment ? `${h.road} · ${h.segment}` : h.road}</b> · MP {fmtMp(h.mpStart)}–{fmtMp(h.mpEnd)}
                      {' '}· {h.n} sites · {h.analyses.join(' · ')}{h.directions ? ` · ${h.directions.join(' ')}` : ''}
                    </Tooltip>
                  );
                  const keep = (el) => { if (el) refs.current[h.id] = el; else delete refs.current[h.id]; };
                  const box = boundsOf(h);
                  return h.coords && h.coords.length > 1 ? (
                    <React.Fragment key={h.id}>
                      {box && <Rectangle bounds={selectionBox(box)} pane="ovHotspots" interactive={false}
                        pathOptions={{ color: HOTSPOT_COLOR, weight: 2, dashArray: '6 4', fillOpacity: 0 }} />}
                      <Polyline ref={keep} positions={h.coords} pane="ovHotspots"
                        pathOptions={{ color: HOTSPOT_COLOR, weight: far ? 14 : 24, opacity: 0.35, lineCap: 'round' }}>{tip}</Polyline>
                    </React.Fragment>
                  ) : (h.lat != null ? (
                    <CircleMarker key={h.id} ref={keep} center={[h.lat, h.lng]} radius={far ? 10 : 16} pane="ovHotspots"
                      pathOptions={{ color: HOTSPOT_COLOR, weight: 0, fillColor: HOTSPOT_COLOR, fillOpacity: 0.35 }}>{tip}</CircleMarker>
                  ) : null);
                })}
                {hotspots.filter((h) => h.lat != null).map((h) => (
                  <Marker key={`rk-${h.id}`} position={[h.lat, h.lng]} pane="ovDecor" interactive={false}
                    icon={rankIcon(h.label || `H${h.rank}`, 'hotspot', false, HOTSPOT_BADGE)} />
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {fadedSites.length > 0 && (
            <LayersControl.Overlay checked name="Outside the project limits">
              <LayerGroup>
                {fadedSites.filter((s) => !only || s.analysis === only).map((s) => (s.coords && s.coords.length > 1 ? (
                  <Polyline key={`fd-${s.id}`} positions={s.coords} pane="ovSites" interactive={false}
                    pathOptions={{ color: colorOf(s), weight: far ? 3 : 5, opacity: 0.25 }} />
                ) : (s.lat != null ? (
                  <CircleMarker key={`fd-${s.id}`} center={[s.lat, s.lng]} radius={5} pane="ovSites" interactive={false}
                    pathOptions={{ color: '#ffffff', weight: 1, fillColor: colorOf(s), fillOpacity: 0.25, opacity: 0.4 }} />
                ) : null)))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          <LayersControl.Overlay checked name="Deficiency sites">
            <LayerGroup>
              {shownSites.map((s) => {
                const seg = segByKey.get(s.segKey);
                const popup = (
                  <SoPopup>
                    <div className="safety-popup">
                      <strong>{s.analysis} · {ANALYSIS_NAMES[s.analysis]}</strong>
                      <div className="safety-popup-mp">
                        {seg ? segmentName(seg) : s.road} · MP {fmtMp(s.mpStart)}{s.lengthMi ? `–${fmtMp(s.mpEnd)} · ${s.lengthMi.toFixed(2)} mi` : ''}
                      </div>
                      <table><tbody>
                        <tr><td>Direction</td><td>{s.directions ? 'Both (one curve)' : s.direction === 'P' ? 'Positive' : 'Negative'}</td></tr>
                        <tr><td>Severity</td><td>{s.severity}</td></tr>
                        <tr><td>Worst</td><td>{s.worstText || '—'}</td></tr>
                        {s.projectId ? <tr><td>Project</td><td>#{s.projectId}</td></tr> : null}
                        <ManeuverRows maneuvers={s.maneuvers} />
                        {rowFlags && rowFlags[s.id] ? (
                          <tr><td>Right of way</td><td>{rowFlags[s.id].pct < 100
                            ? `${rowFlags[s.id].pct}% of the sight line is inside it: the obstruction may be on private land`
                            : '100% of the sight line is inside it: the obstruction is likely on state land'}</td></tr>
                        ) : null}
                      </tbody></table>
                      {onNote && (
                        <div className="so-note-pop">
                          {notes && notes[s.id] ? (
                            <>
                              <div><StatusChip status={notes[s.id].status} /> <span className="safety-muted">{notes[s.id].author} · {notes[s.id].at}</span></div>
                              {notes[s.id].note && <div className="so-note-pop-text">{notes[s.id].note}</div>}
                            </>
                          ) : null}
                          <button type="button" className="safety-btn safety-btn-ghost" onClick={() => onNote(s)}>
                            <i className="fa-solid fa-pen-to-square" aria-hidden="true" /> {notes && notes[s.id] ? 'Edit note' : 'Add note'}
                          </button>
                        </div>
                      )}
                      <div className="safety-popup-actions">
                        {/* ISD: one page per maneuver — link every maneuver that is short, worst first */}
                        <ManeuverButtons maneuvers={s.maneuvers} fallbackUrl={s.reportUrl}
                          fallbackLabel={`Open ${s.analysis} report at MP ${fmtMp(s.mpStart)}`} />
                      </div>
                    </div>
                  </SoPopup>
                );
                const tip = <Tooltip sticky>{s.analysis} · {seg ? segmentName(seg) : s.road} · MP {fmtMp(s.mpStart)} · {s.severity}</Tooltip>;
                const keep = (el) => { if (el) refs.current[s.id] = el; else delete refs.current[s.id]; };
                return s.coords && s.coords.length > 1 ? (
                  <Polyline key={s.id} ref={keep} positions={s.coords} pane="ovSites" eventHandlers={siteEvents(s.analysis)}
                    pathOptions={withStations.has(s.analysis)
                      // a HALO under the station marks, in the run's severity colour: it shows where the
                      // (severity-filtered) deficiency runs are and is the click target for their popup
                      ? { color: SEVERITY_COLORS[s.severity] || '#c92a2a', weight: far ? 7 : 13, opacity: 0.38 }
                      : { color: colorOf(s), weight: far ? 3 : 5, opacity: 0.95 }}>{tip}{popup}</Polyline>
                ) : (s.lat != null ? (
                  <CircleMarker key={s.id} ref={keep} center={[s.lat, s.lng]} radius={6} pane="ovSites" eventHandlers={siteEvents(s.analysis)}
                    pathOptions={{ color: '#ffffff', weight: 1.5, fillColor: colorOf(s), fillOpacity: 0.95 }}>{tip}{popup}</CircleMarker>
                ) : null);
              })}
            </LayerGroup>
          </LayersControl.Overlay>

          {showIntersections && intersections.length > 0 && (
            <LayersControl.Overlay checked name="Intersections (ISD)">
              <LayerGroup>
                {(only && only !== 'ISD' ? [] : intersections).filter((ix) => ix.lat != null).map((ix) => (
                  <CircleMarker key={ix.id} center={[ix.lat, ix.lng]} radius={5} pane="ovIntersections" eventHandlers={siteEvents('ISD')}
                    ref={(el) => { if (el) refs.current[ix.id] = el; else delete refs.current[ix.id]; }}
                    pathOptions={ix.severity
                      ? { color: '#ffffff', weight: 1.5, fillColor: SEVERITY_COLORS[ix.severity], fillOpacity: 0.95 }
                      : { color: ANALYSIS_COLORS.ISD, weight: 2, fillColor: '#0b1222', fillOpacity: 0.9 }}>
                    <Tooltip>Intersection {ix.name || ''} · MP {fmtMp(ix.mp)}{ix.severity ? ` · sight distance short (${ix.severity})` : ' · sight distance met'}</Tooltip>
                    <SoPopup>
                      <div className="safety-popup">
                        <strong>Intersection {ix.name || ''}</strong>
                        <div className="safety-popup-mp">{ix.segName} · MP {fmtMp(ix.mp)}</div>
                        <table><tbody>
                          {ix.isdFt ? <tr><td>Required sight distance</td><td>{Math.round(ix.isdFt)} ft</td></tr> : null}
                          {ix.quality != null ? <tr><td>Worst quality</td><td>{(ix.quality * 100).toFixed(0)}%</td></tr> : null}
                          <tr><td>Result</td><td>{ix.severity ? `short of required · ${ix.severity}` : 'meets the required distance'}</td></tr>
                          <ManeuverRows maneuvers={ix.maneuvers} />
                        </tbody></table>
                        {withStations.has('ISD') && <RaySwitches maneuvers={ix.maneuvers} isd={isd} />}
                        <div className="safety-popup-actions">
                          <ManeuverButtons maneuvers={ix.maneuvers} fallbackUrl={ix.reportUrl}
                            fallbackLabel={`Open ISD report at MP ${fmtMp(ix.mp)}`} />
                        </div>
                      </div>
                    </SoPopup>
                  </CircleMarker>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {topNav && ((topNav.sites && topNav.sites.value > 0) || (topNav.clusters && topNav.clusters.value > 0)) && (
            <LayersControl.Overlay checked name="Top ranks">
              <LayerGroup>
                {topNav.sites && topNav.sites.value > 0 && shownSites.filter((s) => s.lat != null).map((s) => (
                  <Marker key={`rk-${s.id}`} position={[s.lat, s.lng]} pane="ovDecor" interactive={false}
                    icon={rankIcon(siteBadge(topNav.sites, s), 'site', topNav.sites.pinned.includes(s.id), ANALYSIS_COLORS[s.analysis])} />
                ))}
                {!only && topNav.clusters && topNav.clusters.value > 0 && clusters.filter((c) => c.lat != null).map((c) => (
                  <Marker key={`rk-${c.id}`} position={[c.lat, c.lng]} pane="ovDecor" interactive={false}
                    icon={rankIcon(`#${topNav.clusters.rank.get(c.id) ?? '—'}`, 'cluster', topNav.clusters.pinned.includes(c.id))} />
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {showClusters && (
          <LayersControl.Overlay checked name="Multi-deficiency clusters">
            <LayerGroup>
              {/* Rings are a close-up aid: zoomed out over a whole order they would bury the roads in white. */}
              {(far || only ? [] : clusters.filter((c) => c.lat != null)).map((c) => (
                <CircleMarker key={c.id} center={[c.lat, c.lng]} radius={mid ? 8 : 12} pane="ovClusters"
                  ref={(el) => { if (el) refs.current[c.id] = el; else delete refs.current[c.id]; }}
                  pathOptions={{ color: '#f8fafc', weight: mid ? 1.4 : 2, fillOpacity: 0, dashArray: '4 3' }}>
                  <Tooltip>{c.n} analyses · {segmentName(c)} · MP {fmtMp(c.mpStart)}</Tooltip>
                  <SoPopup>
                    <div className="safety-popup">
                      <strong>{c.n} analyses at one place</strong>
                      <div className="safety-popup-mp">{segmentName(c)} · MP {fmtMp(c.mpStart)}–{fmtMp(c.mpEnd)}</div>
                      <table><tbody>
                        <tr><td>Analyses</td><td>{c.analyses.join(' · ')}</td></tr>
                        <tr><td>Sites</td><td>{c.sites}</td></tr>
                        <tr><td>Worst severity</td><td>{c.severity}</td></tr>
                      </tbody></table>
                      <div className="safety-popup-actions">
                        {Object.entries(c.reports).map(([a, site]) => (
                          <ReportButton key={a} url={site.reportUrl} label={`Open ${a} report at MP ${fmtMp(site.mpStart)}`} />
                        ))}
                      </div>
                    </div>
                  </SoPopup>
                </CircleMarker>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>
          )}

          {network && network.length > 0 && (
            <LayersControl.Overlay checked name="Road network (SOP)">
              <LayerGroup>
                {/* a close-up aid like the cluster rings: crosswalks alone would bury a whole order */}
                {/* existing guardrails: a steel line along the road on their side of travel */}
                {network.filter((f) => f.type === 'guardrail' && (f.coords || []).length > 1).map((f) => (
                  <React.Fragment key={`${f.id}-line`}>
                  <Polyline positions={f.coords} pane="ovRails" interactive={false}
                    pathOptions={{ color: '#ffffff', weight: far ? 5 : 8, opacity: 0.85, lineCap: 'butt' }} />
                  <Polyline positions={f.coords} pane="ovRails"
                    pathOptions={{ color: GUARDRAIL_COLOR, weight: far ? 3 : 5, opacity: 1, lineCap: 'butt' }}>
                    <Tooltip sticky>{GUARDRAIL_LABEL} · {f.label} · {NET_SOURCE_NAME[f.src]}{f.srcLabel ? ` (${f.srcLabel})` : ''}
                      {f.side === 'P' ? ' · positive-direction side' : f.side === 'N' ? ' · negative-direction side' : ' · centre'}</Tooltip>
                    <SoPopup>{netPopup(f)}</SoPopup>
                  </Polyline>
                  </React.Fragment>
                ))}
                {(far ? [] : network.filter((f) => f.lat != null && f.type !== 'guardrail')).map((f) => (
                  <Marker key={f.id} position={[f.lat, f.lng]} icon={netIcon(f)}
                    ref={(el) => { if (el) refs.current[f.id] = el; else delete refs.current[f.id]; }}>
                    <Tooltip>{NET_LABEL[f.type]} · {NET_SOURCE_NAME[f.src]}</Tooltip>
                    <SoPopup>{netPopup(f)}</SoPopup>
                  </Marker>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {crashes && (
            <LayersControl.Overlay checked name="Crashes">
              <LayerGroup>
                {crashes.filter((c) => c.lat != null).map((c) => (
                  <React.Fragment key={c.id}>
                    {c.glareMatch && (
                      <CircleMarker center={[c.lat, c.lng]} radius={10} pane="ovCrashes" interactive={false}
                        pathOptions={{ color: '#e8a30c', weight: 2.5, fillOpacity: 0 }} />
                    )}
                    <CircleMarker center={[c.lat, c.lng]} pane="ovCrashes" radius={crashRadius(c)} pathOptions={crashPathOptions(c)}
                      ref={(el) => { if (el) refs.current[c.id] = el; else delete refs.current[c.id]; }}>
                      <SoPopup><CrashPopupBody crash={c} analysis={crashAnalysis} recommendations={recommendations} /></SoPopup>
                    </CircleMarker>
                  </React.Fragment>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}
          {crashes && (
            <LayersControl.Overlay checked name="Crash clusters">
              <LayerGroup>
                {crashClusters.filter((cl) => cl.lat != null).map((cl) => (
                  <CircleMarker key={cl.id} center={[cl.lat, cl.lng]} radius={mid ? 12 : 18} pane="ovCrashClusters"
                    ref={(el) => { if (el) refs.current[cl.id] = el; else delete refs.current[cl.id]; }}
                    pathOptions={{ color: '#6b2e8a', weight: 2.5, fillColor: '#6b2e8a', fillOpacity: 0.07, dashArray: '4 3' }}>
                    <Tooltip>{cl.count} crashes · {cl.road} · MP {fmtMp(cl.mpStart)}{cl.mpEnd > cl.mpStart ? `–${fmtMp(cl.mpEnd)}` : ''}</Tooltip>
                    <SoPopup>
                      <div className="safety-popup">
                        <strong>Crash cluster · {cl.count} crashes</strong>
                        <div className="safety-popup-mp">{cl.road} · MP {fmtMp(cl.mpStart)}{cl.mpEnd > cl.mpStart ? `–${fmtMp(cl.mpEnd)}` : ''}{cl.towns ? ` · ${cl.towns}` : ''}</div>
                        <table><tbody>
                          <tr><td>Fatal · injury · property damage</td><td>{cl.mix ? `${cl.mix.fatal} · ${cl.mix.injury} · ${cl.mix.pdo}` : '—'}</td></tr>
                          <tr><td>Leading type</td><td>{cl.leading || '—'}</td></tr>
                          {cl.years ? <tr><td>Years</td><td>{cl.years}</td></tr> : null}
                          <tr><td>Deficiency sites near</td><td>{cl.hit ? cl.hitAnalyses.join(' · ') : 'none: the analyses miss this cluster'}</td></tr>
                        </tbody></table>
                        <ClusterCrashes members={cl.members || []} analysis={crashAnalysis} recommendations={recommendations} />
                        {(cl.nearSites || []).length > 0 && (
                          <div className="safety-popup-actions">
                            {cl.nearSites.map((s) => (
                              <ManeuverButtons key={s.id} maneuvers={s.maneuvers} fallbackUrl={s.reportUrl}
                                fallbackLabel={`Open ${s.analysis} report at MP ${fmtMp(s.mpStart)}`} />
                            ))}
                          </div>
                        )}
                      </div>
                    </SoPopup>
                  </CircleMarker>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}
        </LayersControl>
        {(places || []).map((p, i) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={PLACE_ICON} zIndexOffset={1000}
            ref={(el) => { if (el) refs.current[p.id] = el; else delete refs.current[p.id]; }}>
            <Tooltip>{`Point of Interest ${i + 1}${p.note ? ` · ${p.note.slice(0, 60)}` : ''}`}</Tooltip>
            <SoPopup><PlacePopup place={p} index={i} onUpdate={onUpdatePlace} onRemove={onRemovePlace} /></SoPopup>
          </Marker>
        ))}
      </MapContainer>
      {places && places.length > 0 && (
        <div className="so-place-nav" role="group" aria-label="Points of Interest">
          <button type="button" aria-label="Previous Point of Interest" onClick={() => goPlace(-1)}>‹</button>
          <span>★ {placeIdx < 0 ? `${places.length} Point${places.length === 1 ? '' : 's'} of Interest` : `Point of Interest ${placeIdx + 1} of ${places.length}`}</span>
          <button type="button" aria-label="Next Point of Interest" onClick={() => goPlace(1)}>›</button>
        </div>
      )}
      {topNav && (
        <TopOnMap nav={topNav} crash={{ list: navClusters, idx: clusterIdx, go: goCluster }}
          onGo={(x, zoom) => setNavFocus({ id: x.id, lat: x.lat, lng: x.lng, zoom, bounds: boundsOf(x), stamp: Date.now() })} />
      )}
      {!topNav && navClusters.length > 0 && (
        <div className="so-cluster-nav" role="group" aria-label="Crash clusters">
          <button type="button" aria-label="Previous crash cluster" onClick={() => goCluster(-1)}>‹</button>
          <span>{clusterIdx < 0 ? `${navClusters.length} crash clusters` : `Crash cluster ${clusterIdx + 1} of ${navClusters.length}`}</span>
          <button type="button" aria-label="Next crash cluster" onClick={() => goCluster(1)}>›</button>
        </div>
      )}
      {far && ((showClusters && clusters.length > 0) || (network && network.length > 0)) && (
        <div className="ov-map-hint">
          {[clusters.length > 0 && `${clusters.length} multi-deficiency clusters`, network && network.length > 0 && `${network.length} road features`]
            .filter(Boolean).join(' · ')} · zoom in to see them
        </div>
      )}
      <Legend showClusters={showClusters} colourBy={runColourBy} analyses={analyses} roads={roads} details={details} showIntersections={showIntersections && intersections.length > 0} showPaths={showPaths}
        showCrashes={Boolean(crashes)} isd={isd} showNetwork={Boolean(network && network.length)}  grwShield={grwShield} />
    </div>
  );
}
